import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, Fragment as _Fragment, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"
import _imports_0 from '@/assets/icons/person_gray_150.png'
import _imports_1 from '@/assets/icons/document.svg'
import _imports_2 from '@/assets/icons/photo_sepia.svg'


const _withScopeId = n => (_pushScopeId("data-v-587fdc49"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 1,
  class: "loading"
}
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", null, "Loading details...", -1))
const _hoisted_3 = { class: "alert alert-primary alert-sm" }
const _hoisted_4 = { class: "stats" }
const _hoisted_5 = { class: "stat" }
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "icon-area" }, [
  /*#__PURE__*/_createElementVNode("div", { class: "people-pair" }, [
    /*#__PURE__*/_createElementVNode("img", {
      src: _imports_0,
      class: "back"
    }),
    /*#__PURE__*/_createElementVNode("img", { src: _imports_0 })
  ])
], -1))
const _hoisted_7 = { class: "count" }
const _hoisted_8 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "label" }, "people", -1))
const _hoisted_9 = { class: "stat" }
const _hoisted_10 = { class: "icon-area" }
const _hoisted_11 = { class: "country-wrap" }
const _hoisted_12 = { class: "count" }
const _hoisted_13 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "label" }, "countries", -1))
const _hoisted_14 = { class: "stat disabled" }
const _hoisted_15 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "icon-area" }, [
  /*#__PURE__*/_createElementVNode("div", { class: "photo-doc-pair" }, [
    /*#__PURE__*/_createElementVNode("img", {
      src: _imports_1,
      class: "doc"
    }),
    /*#__PURE__*/_createElementVNode("img", { src: _imports_2 })
  ])
], -1))
const _hoisted_16 = { class: "count" }
const _hoisted_17 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "label" }, "photos & documents", -1))
const _hoisted_18 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", null, "When you upload your genealogy database file directly, only the data in the file is included. It does not upload any photos, documents, or other external files.", -1))
const _hoisted_19 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", null, "If you use the Sync app to upload your tree, it can upload your photos, documents, and external files automatically.", -1))

import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { usePageTitle, unexpectedErrorEvent, hideModalAsync } from '@webapp/util/AppUtil'
import { useTelemetry } from '@webapp/util/Telemetry'
import { useSyncSessionStore } from './SyncSessionStore'
import { SyncScope, toSyncSource } from './SyncSessionModel'
import { ItemType, ValueCount } from '@/rd/ResearchDataModel'
import { CountryCodes } from '@/util/GeoUtil'
import TaskPage from '@webapp/util/TaskPage.vue'
import LoadingIndicator from '@webapp/util/LoadingIndicator.vue'
import CountryCloud from '@/explore/places/CountryCloud.vue'
import Modal from '@webapp/util/Modal.vue'
import SyncSourceHeader from './SyncSourceHeader.vue'
import LivingPeopleModal from './LivingPeopleModal.vue'
import SyncOptionsModal from './SyncOptionsModal.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'SyncOptionsPage',
  props: {
    sessionId: {}
  },
  emits: ['started', 'back', 'unusable'],
  setup(__props: any, { emit }) {

const props = __props;





const router = useRouter()
const syncSessionStore = useSyncSessionStore()
const { trackException } = useTelemetry()

const isLoadingRef = ref(true)
const livingPeopleModalRef = ref<InstanceType<typeof LivingPeopleModal>>()
const syncOptionsModalRef = ref<InstanceType<typeof SyncOptionsModal>>()
const taskPageRef = ref<InstanceType<typeof TaskPage>>()
const noContentModalRef = ref<InstanceType<typeof Modal>>()
const sessionRef = computed(() => syncSessionStore.getSession(props.sessionId))
const sourceRef = computed(() => sessionRef.value ? toSyncSource(sessionRef.value) : undefined)

const sourceDetailsRef = computed(() => syncSessionStore.getState(props.sessionId)?.sourceDetails)
const personCountRef = computed(() => sourceDetailsRef.value?.itemCounts[ItemType.Person]?.total ?? 0)
const contentCountRef = computed(() => sourceDetailsRef.value?.itemCounts[ItemType.Content]?.total ?? 0)
const topCountriesRef = computed<ValueCount[]>(() =>
  Object.entries(sourceDetailsRef.value?.birthCountryCounts ?? {})
    .map(([name, count]) => ({ value: CountryCodes.fromName(name), count }))
    .filter((c): c is ValueCount => !!c.value)
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value)),
)
const countryCountRef = computed(() => topCountriesRef.value.length)

function showLivingPeople() {
  livingPeopleModalRef.value?.open()
}

function showSyncOptions() {
  syncOptionsModalRef.value?.open()
}

async function onSyncAppHelp() {
  await hideModalAsync(noContentModalRef.value?.$el)
  router.push('/learn/sync')
}

usePageTitle('Upload my tree')

onMounted(async () => {
  // Returning here after a failed sync: the source data and the user's choices are still
  // loaded, so keep them rather than reloading and resetting the scope to defaults.
  if (syncSessionStore.getState(props.sessionId)?.sourceDetails) {
    isLoadingRef.value = false
    return
  }
  try {
    await syncSessionStore.refreshSessionAsync(props.sessionId)
    await syncSessionStore.loadSourceDetailsAsync(props.sessionId, new SyncScope())
    await syncSessionStore.loadLivingWindowGraphAsync(props.sessionId)
    isLoadingRef.value = false
  }
  catch (error) {
    // The server cleans up an idle session after a while -- resuming a cleaned-up one fails. Hand
    // back to the wizard so it can forget the session and let the user re-upload.
    trackException(error)
    emit('unusable')
  }
})

function onBack() {
  emit('back')
}

function startOrResumeSyncAsync() {
  if (syncSessionStore.getSession(props.sessionId)?.workspaceId) {
    return syncSessionStore.retrySyncAsync(props.sessionId)
  }
  return syncSessionStore.startSyncAsync(props.sessionId)
}

async function onNext() {
  try {
    await startOrResumeSyncAsync()
    emit('started')
  }
  catch (error) {
    taskPageRef.value?.setBusy(false)
    unexpectedErrorEvent.trigger({ error })
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(TaskPage, {
    ref_key: "taskPageRef",
    ref: taskPageRef,
    "no-brand": "",
    "no-auto-back": "",
    "can-continue": !isLoadingRef.value,
    onAction: onNext,
    onBack: onBack
  }, {
    title: _withCtx(() => [
      _createTextVNode("Upload my tree")
    ]),
    secondaryAction: _withCtx(() => [
      _createElementVNode("button", {
        type: "button",
        class: "btn btn-outline-primary",
        onClick: showSyncOptions
      }, "Options")
    ]),
    backText: _withCtx(() => [
      _createTextVNode("Cancel")
    ]),
    actionText: _withCtx(() => [
      _createTextVNode("Create online tree")
    ]),
    default: _withCtx(() => [
      (sourceRef.value)
        ? (_openBlock(), _createBlock(SyncSourceHeader, {
            key: 0,
            source: sourceRef.value
          }, null, 8, ["source"]))
        : _createCommentVNode("", true),
      (isLoadingRef.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(LoadingIndicator),
            _hoisted_2
          ]))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
            _createElementVNode("div", _hoisted_3, [
              _createTextVNode(" Photos and documents will not be included. "),
              _createElementVNode("button", {
                type: "button",
                class: "btn btn-inline btn-link",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (noContentModalRef.value?.open()))
              }, "Why not?")
            ]),
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _hoisted_6,
                _createElementVNode("div", _hoisted_7, _toDisplayString(personCountRef.value.toLocaleString()), 1),
                _hoisted_8
              ]),
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("div", _hoisted_11, [
                    _createVNode(CountryCloud, { countries: topCountriesRef.value }, null, 8, ["countries"])
                  ])
                ]),
                _createElementVNode("div", _hoisted_12, _toDisplayString(countryCountRef.value.toLocaleString()), 1),
                _hoisted_13
              ]),
              _createElementVNode("div", _hoisted_14, [
                _hoisted_15,
                _createElementVNode("div", _hoisted_16, _toDisplayString(contentCountRef.value.toLocaleString()), 1),
                _hoisted_17
              ])
            ])
          ], 64)),
      _createVNode(LivingPeopleModal, {
        ref_key: "livingPeopleModalRef",
        ref: livingPeopleModalRef,
        "session-id": _ctx.sessionId
      }, null, 8, ["session-id"]),
      _createVNode(SyncOptionsModal, {
        ref_key: "syncOptionsModalRef",
        ref: syncOptionsModalRef,
        "session-id": _ctx.sessionId,
        onEditLiving: showLivingPeople
      }, null, 8, ["session-id"]),
      _createVNode(Modal, {
        ref_key: "noContentModalRef",
        ref: noContentModalRef,
        info: ""
      }, {
        title: _withCtx(() => [
          _createTextVNode("Photos & documents")
        ]),
        okText: _withCtx(() => [
          _createTextVNode("Got it")
        ]),
        default: _withCtx(() => [
          _hoisted_18,
          _hoisted_19,
          _createElementVNode("button", {
            type: "button",
            class: "btn btn-inline btn-link",
            onClick: onSyncAppHelp
          }, "Learn more about the Sync app")
        ]),
        _: 1
      }, 512)
    ]),
    _: 1
  }, 8, ["can-continue"]))
}
}

})